import { memo, useCallback, useRef, useState } from "react";
import { Controller, useFormContext } from "react-hook-form";
import {
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  Radio,
  RadioGroup,
  FormLabel,
  FormGroup,
  FormHelperText,
  Button,
  InputAdornment,
  IconButton,
  Typography,
  Box,
  Stack,
  Chip,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import TextField from "../../../../bmr/components/styled/FormField";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import Switch from "../../../../bmr/components/styled/Switch";
import Select from "../../../../bmr/components/styled/SelectField";
import {
  LocalizationProvider,
  TimePicker,
  DatePicker,
} from "@mui/x-date-pickers";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
// import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
// import { SingleInputDateRangeField } from "@mui/x-date-pickers-pro/SingleInputDateRangeField";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localizedFormat from "dayjs/plugin/localizedFormat";
import { DeleteIcon } from "../../../../assets/common-svg/CommonSvg";
import CustomDateRangePicker from "../../../../components/custom-date-picker/CustomDateRangePicker";
import AppSwitch from "../../../../bmr/components/styled/Switch/AppSwitch";

dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const VALID_DATE_FORMATS = [
  "DD/MM/YY",
  "MM/DD/YY",
  "YY/MM/DD",
  "DD-MM-YY",
  "MM-DD-YY",
  "YY-MM-DD",
  "DD/MM/YYYY",
  "YYYY-MM-DD",
  "MM",
  "YYYY",
  "MM/YYYY",
  "MM-YYYY",
  "MM/YY",
  "MM-YY",
];
const VALID_TIME_FORMATS = ["hh:mm A", "HH:mm"];

// ---------------------------------------------------------------------------
// Pure helpers (defined outside the component so they are never recreated)
// ---------------------------------------------------------------------------

/**
 * Validate a date format string; return the fallback when it is not in the
 * allow-list or is falsy.
 */
const resolveDateFormat = (fmt, fallback = "DD/MM/YY") =>
  fmt && VALID_DATE_FORMATS.includes(fmt) ? fmt : fallback;

/**
 * Validate a time format string; return the fallback when not in allow-list.
 */
const resolveTimeFormat = (fmt, fallback = "HH:mm") =>
  fmt && VALID_TIME_FORMATS.includes(fmt) ? fmt : fallback;

/**
 * Parse a string value into a dayjs object using the given format.
 * Returns null (not undefined) when the value is empty or invalid so MUI
 * pickers receive a controlled null rather than an uncontrolled undefined.
 */
const safeParse = (value, format) => {
  if (!value) return null;
  // Try strict mode with provided format first (useful for raw UI inputs)
  let d = dayjs(value, format, true);
  if (d.isValid()) return d;

  // Fallback 1: Parse using any valid format from VALID_DATE_FORMATS strictly
  d = dayjs(value, VALID_DATE_FORMATS, true);
  if (d.isValid()) return d;

  // Fallback 2: General parsing (catches ISO YYYY-MM-DD from backend)
  d = dayjs(value);
  return d.isValid() ? d : null;
};

// ---------------------------------------------------------------------------
// Sub-component: placeholder text for Select renderValue
// ---------------------------------------------------------------------------
const PlaceholderText = ({ placeholder }) => (
  <span style={{ color: "#9e9e9e" }}>{placeholder}</span>
);

// ---------------------------------------------------------------------------
// FormField
// ---------------------------------------------------------------------------
const FormField = memo(
  ({
  name,
  type = "text",
  label = "",
  placeholder,
  options = [],
  fullWidth = true,
  rows = 4,
  disabled = false,
  row = "",
  description = "",
  inputLabelTop = "",
  rules = {},
  size = "small",
  // CUSTOM PROPS
  futureOnly,
  pastOnly,
  currentDate,
  defaultDate,
  currentDateTime,
  defaultDateTime,
  // dateReadonly,
  dateFormat,
  dateRange,
  minDate,
  maxDate,
  combineDate,
  combineTime,
  timeFormat,
  currentTime,
  defaultTime,
  minDateTime,
  maxDateTime,
  linkLabel,
  linkUrl,
  endAdornment,
  label1,
  label2,
  nomargin,
  searchable,
  ...props
  }) => {
    const [showPassword, setShowPassword] = useState(false);
    const {
      formState: { submitCount = 0 } = {},
    } = useFormContext();
    const handleTogglePassword = useCallback(
      () => setShowPassword((p) => !p),
      [],
    );
    const inputRef = useRef(null);

    const normalizeDateFormat = (format = "") => {
      if (!format) return "DD/MM/YY";

      return format.replace(/:/g, ":").replace(/-/g, "-").replace(/\s+/g, "");
    };
    const renderField = useCallback(
      (field, fieldState) => {
        const helperText =
          props.helperText ??
          fieldState?.error?.message ??
          (typeof props.error === "string" ? props.error : "");
        const hasError =
          Boolean(props.error) ||
          Boolean(
            fieldState?.error &&
              (fieldState.isTouched ||
                fieldState.isDirty ||
                submitCount > 0),
          );

        switch (type) {
          // ── Label ────────────────────────────────────────────────────────
          case "label":
            return (
              <Typography
                variant="body1"
                sx={{ fontWeight: 700, color: "text.secondary" }}
              >
                {field.label}
              </Typography>
            );

          // ── Text / Email ─────────────────────────────────────────────────
          case "text":
          case "email":
            return (
              <>
                <Tooltip
                  title={
                    props?.activeInput !== name ? `${field?.value || ""}` : ""
                  }
                  arrow
                  placement="bottom-start"
                  disableInteractive
                >
                  <TextField
                    {...field}
                    type={type}
                    placeholder={placeholder}
                    fullWidth={fullWidth}
                    error={hasError}
                    helperText={helperText}
                    disabled={disabled}
                    onFocus={() => props?.setActiveInput?.(name)}
                    {...props}
                    onChange={(e) => {
                      field.onChange(e);
                      props.onChange?.(e);
                    }}
                    onBlur={(e) => {
                      props?.setActiveInput?.(null);
                      field.onBlur();
                      props.onBlur?.(e);
                    }}
                    InputProps={{
                      classes: { input: props.className },
                      endAdornment: endAdornment,
                    }}
                    sx={{
                      ...(hasError  && {
                        "& .MuiInputBase-input": {
                          border: "1px solid #ff2023",
                        },
                      }),
                    }}
                  />
                </Tooltip>
              </>
            );

          // ── Number ───────────────────────────────────────────────────────
          case "number":
            return (
              <TextField
                {...field}
                type="number"
                label={label}
                placeholder={placeholder}
                fullWidth={fullWidth}
                error={hasError}
                helperText={helperText}
                disabled={disabled}
                onChange={(e) => {
                  const value =
                    e.target.value === "" ? "" : Number(e.target.value);
                  field.onChange(value);
                  props.onChange?.(e);
                }}
                onBlur={(e) => {
                  field.onBlur();
                  props.onBlur?.(e);
                }}
                InputProps={{ endAdornment: endAdornment }}
                {...props}
                sx={{
                  "& .MuiInputBase-input": { paddingRight: "80px" },
                  ...(hasError  && {
                    "& .MuiInputBase-input": { border: "1px solid #ff2023" },
                  }),
                }}
              />
            );

          // ── Password ─────────────────────────────────────────────────────
          case "password":
            return (
              <TextField
                {...field}
                type={showPassword ? "text" : "password"}
                label={label}
                placeholder={placeholder}
                fullWidth={fullWidth}
                error={hasError}
                helperText={helperText}
                disabled={disabled}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={handleTogglePassword}
                        edge="end"
                        aria-label="toggle password visibility"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                {...props}
                onChange={(e) => {
                  field.onChange(e);
                  props.onChange?.(e);
                }}
                onBlur={(e) => {
                  field.onBlur();
                  props.onBlur?.(e);
                }}
              />
            );

          // ── Textarea ─────────────────────────────────────────────────────
          case "textarea":
            return (
              <TextField
                {...field}
                label={label}
                placeholder={placeholder}
                fullWidth={fullWidth}
                multiline
                rows={rows}
                className="mb-3"
                size="small"
                error={hasError}
                helperText={helperText}
                disabled={disabled}
                {...props}
                onChange={(e) => {
                  field.onChange(e);
                  props.onChange?.(e);
                }}
                onBlur={(e) => {
                  field.onBlur();
                  props.onBlur?.(e);
                }}
              />
            );

          // ── Select ───────────────────────────────────────────────────────
          case "select": {
            const selectValue =
              props?.value !== undefined ? props.value : field.value;

            if (searchable) {
              const selectedOption = props?.multiple
                ? (options || []).filter((o) => (selectValue || []).includes(o.value))
                : (options || []).find((o) => o.value === selectValue) || null;

              return (
                <FormControl
                  fullWidth={fullWidth}
                  error={hasError}
                  disabled={disabled}
                  className="controlledSelect"
                >
                  <Autocomplete
                    {...props}
                    multiple={props?.multiple}
                    options={options || []}
                    getOptionLabel={(option) => option?.label || ""}
                    value={selectedOption}
                    isOptionEqualToValue={(option, value) => option.value === value.value}
                    forcePopupIcon={false}
                    onChange={(event, newValue) => {
                      let newVal;
                      if (props?.multiple) {
                        newVal = newValue ? newValue.map((v) => v.value) : [];
                      } else {
                        newVal = newValue ? newValue.value : "";
                      }
                      field.onChange(newVal);
                      props.onChange?.(newVal);
                    }}
                    onBlur={(e) => {
                      field.onBlur();
                      props.onBlur?.(e);
                    }}
                    disabled={disabled}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        placeholder={placeholder}
                        error={hasError}
                        sx={{
                          "& .MuiInputBase-input": {
                            border: "none !important",
                          }
                        }}
                      />
                    )}
                  />
                  {hasError && <FormHelperText>{helperText}</FormHelperText>}
                </FormControl>
              );
            }

            return (
              <FormControl
                fullWidth={fullWidth}
                error={hasError}
                disabled={disabled}
                className="controlledSelect"
              >
                <Select
                  {...field}
                  {...props}
                  displayEmpty
                  value={
                    props?.multiple ? selectValue ?? [] : selectValue ?? ""
                  }
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    props.onChange?.(e);
                  }}
                  onBlur={(e) => {
                    field.onBlur();
                    props.onBlur?.(e);
                  }}
                  MenuProps={{
                    anchorOrigin: { vertical: "bottom", horizontal: "left" },
                    transformOrigin: { vertical: "top", horizontal: "left" },
                    PaperProps: { style: { minWidth: "12.5rem" } },
                    disablePortal: false,
                  }}
                  renderValue={(selected) => {
                    const isEmpty = props?.multiple
                      ? !selected?.length
                      : selected === "" || selected == null;

                    if (isEmpty)
                      return <PlaceholderText placeholder={placeholder} />;
                    if (props?.renderValue) return props.renderValue(selected);

                    if (props?.multiple) {
                      const arr = Array.isArray(selected)
                        ? selected
                        : [selected];
                      return (
                        <Box
                          sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                        >
                          {arr.map((val) => (
                            <Chip
                              key={val}
                              label={
                                options?.find((o) => o?.value === val)?.label ||
                                val
                              }
                              size="small"
                            />
                          ))}
                        </Box>
                      );
                    }

                    return (
                      options?.find((o) => o?.value === selected)?.label ||
                      selected
                    );
                  }}
                >
                  <MenuItem disabled value="">
                    <em>{placeholder}</em>
                  </MenuItem>
                  {options?.map((option) => (
                    <MenuItem
                      key={option?.value}
                      value={option?.value}
                      disabled={option?.disabled}
                    >
                      {props?.multiple && (
                        <Checkbox
                          checked={
                            Array.isArray(selectValue) &&
                            selectValue.includes(option?.value)
                          }
                          size="small"
                          sx={{ mr: 1, p: 0 }}
                        />
                      )}
                      {option?.label}
                    </MenuItem>
                  ))}
                </Select>
                {hasError && <FormHelperText>{helperText}</FormHelperText>}
              </FormControl>
            );
          }

          //@info remove later this comment
          // ── Switch ───────────────────────────────────────────────────────
          // case "switch": {
          //   const { label1, label2 } = props || {};
          //   const hasbothlabels = label1 || label2;
          //   return (
          //     <FormControl error={hasError} disabled={disabled}>
          //       {hasbothlabels ? (
          //         <Stack direction="row" alignItems="center" spacing={1}>
          //           <Typography className="text-sm font-medium">
          //             {label1}
          //           </Typography>
          //           <AppSwitch
          //             {...props}
          //             {...field}
          //             checked={!!field.value}
          //             onChange={(e, checked) => {
          //               field.onChange(checked);
          //               props.onChange?.(e);
          //             }}
          //           />
          //           <Typography className="text-sm font-medium">
          //             {label2}
          //           </Typography>
          //         </Stack>
          //       ) : (
          //         <FormControlLabel
          //           className="font-medium text-sm ml-0"
          //           control={
          //             <Switch
          //               {...props}
          //               {...field}
          //               checked={!!field.value}
          //               onChange={(e, checked) => {
          //                 field.onChange(checked);
          //                 props.onChange?.(e);
          //               }}
          //             />
          //           }
          //           label={label}
          //         />
          //       )}
          //       {hasError && <FormHelperText>{props.helperText}</FormHelperText>}
          //     </FormControl>
          //   );
          // }

          case "switch": {
            const hasbothlabels = label1 || label2;

            return (
              <FormControl error={hasError} disabled={disabled}>
                {hasbothlabels ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography className="text-sm font-medium">{label1}</Typography>

                      <AppSwitch
                        checked={!!field.value}
                        onChange={(e, checked) => {
                          field.onChange(checked);
                          props.onChange?.(checked);
                        }}
                        onBlur={(e) => {
                          field.onBlur();
                          props.onBlur?.(e);
                        }}
                      />

                    <Typography className="text-sm font-medium">{label2}</Typography>
                  </Stack>
                ) : (
                  <FormControlLabel
                    className="font-medium text-sm ml-0"
                    control={
                      <Switch
                        checked={!!field.value}
                        onChange={(e, checked) => {
                          field.onChange(checked);
                          props.onChange?.(checked);
                        }}
                        onBlur={(e) => {
                          field.onBlur();
                          props.onBlur?.(e);
                        }}
                      />
                    }
                    label={label}
                  />
                )}
                {hasError && <FormHelperText>{helperText}</FormHelperText>}
              </FormControl>
            );
          }
          // ── Checkbox group ────────────────────────────────────────────────
          case "checkbox-group":
            return (
              <FormControl
                error={hasError}
                disabled={disabled}
                component="fieldset"
              >
                <FormLabel component="legend">{label}</FormLabel>
                <FormGroup row={!!row}>
                  {options.map((option) => (
                    <FormControlLabel
                      key={option.value}
                      control={
                        <Checkbox
                          checked={(field.value || []).includes(option.value)}
                          onChange={(e) => {
                            let nv = field.value || [];
                            nv = e.target.checked
                              ? [...nv, option.value]
                              : nv.filter((v) => v !== option.value);
                            field.onChange(nv);
                            props.onChange?.(nv);
                          }}
                          onBlur={(e) => {
                            field.onBlur();
                            props.onBlur?.(e);
                          }}
                        />
                      }
                      label={option.label}
                    />
                  ))}
                </FormGroup>
                {hasError && <FormHelperText>{helperText}</FormHelperText>}
              </FormControl>
            );

          // ── Radio group ───────────────────────────────────────────────────
          case "radio-group":
            return (
              <FormControl
                error={hasError}
                disabled={disabled}
                component="fieldset"
                sx={{ display: "block" }}
              >
                {label && (
                  <FormLabel component="legend" className="text-xs">
                    {label}
                  </FormLabel>
                )}
                <RadioGroup
                  row={row}
                  value={field.value ?? ""}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    props.onChange?.(e);
                  }}
                  onBlur={(e) => {
                    field.onBlur();
                    props.onBlur?.(e);
                  }}
                  className=""
                >
                  {options.map((option) => (
                    <FormControlLabel
                      key={String(option.value)}
                      value={option.value}
                      control={<Radio size="small" color="primary" />}
                      label={option.label}
                    />
                  ))}
                </RadioGroup>
                {hasError && <FormHelperText>{helperText}</FormHelperText>}
              </FormControl>
            );

          // ── File / Image / Barcode ────────────────────────────────────────
          case "file":
          case "image_upload":
          case "barcode": {
            const handleFileChange = (e) => {
              const file = e?.target?.files?.[0] || null;
              field.onChange(file);
              e.target.value = "";
            };
            const handleRemoveFile = () => {
              field.onChange(null);
              if (inputRef.current) inputRef.current.value = "";
            };
            return (
              <FormControl
                fullWidth={fullWidth}
                error={hasError}
                disabled={disabled}
              >
                <input
                  ref={inputRef}
                  {...props}
                  type="file"
                  id={name}
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                  onBlur={(e) => {
                    field.onBlur();
                    props.onBlur?.(e);
                  }}
                />
                <Box display="flex" gap={1} alignItems="center">
                  <label htmlFor={name} style={{ flex: 1 }}>
                    <Button
                      variant="outlined"
                      component="span"
                      fullWidth
                      disabled={disabled}
                    >
                      {field?.value?.name || label || "Choose File"}
                    </Button>
                  </label>
                  {field?.value && (
                    <IconButton
                      edge="end"
                      aria-label="delete"
                      onClick={handleRemoveFile}
                      size="small"
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
                {hasError && <FormHelperText>{helperText}</FormHelperText>}
              </FormControl>
            );
          }

          case "date": {
            const dateFormates = resolveDateFormat(dateFormat);
            const constraints = {};
            const today = dayjs();

            // Flexible parse — ISO ya any standard format
            const parsedMinDate = minDate ? dayjs(minDate) : null;
            const parsedMaxDate = maxDate ? dayjs(maxDate) : null;

            const allowFuture = futureOnly === true;
            const allowPast = pastOnly === true;

            if (allowFuture && !allowPast) {
              constraints.minDate = today;
            } else if (allowPast && !allowFuture) {
              constraints.maxDate = today;
            }
            // dono true ya dono false — koi constraint nahi

            // User-defined min/max ke saath merge
            if (parsedMinDate?.isValid()) {
              constraints.minDate = constraints.minDate?.isAfter(parsedMinDate)
                ? constraints.minDate
                : parsedMinDate;
            }

            if (parsedMaxDate?.isValid()) {
              constraints.maxDate = constraints.maxDate?.isBefore(parsedMaxDate)
                ? constraints.maxDate
                : parsedMaxDate;
            }

            // Range picker
            if (dateRange) {
              const rangeValue =
                field.value && typeof field.value === "object"
                  ? field.value
                  : { start: null, end: null };

              const handleDateChange = (date) => {
                const formatted = {
                  start: date?.from || null,
                  end: date?.to || null,
                };
                field.onChange(formatted);
                props.onChange?.(formatted);
              };

              return (
                <FormControl
                  fullWidth={fullWidth}
                  error={hasError}
                  disabled={disabled}
                >
                  <CustomDateRangePicker
                    selectedDate={{
                      from: rangeValue.start,
                      to: rangeValue.end,
                    }}
                    handleDateChange={handleDateChange}
                    placeholder={placeholder || "Select Date Range"}
                    minDate={constraints.minDate?.toDate()}
                    maxDate={constraints.maxDate?.toDate()}
                    daterangePosition="showdaterangefull"
                  />
                  {hasError && <FormHelperText>{helperText}</FormHelperText>}
                </FormControl>
              );
            }

            // Single date picker
            let finalValue = field.value;
            if (currentDate && !field.value) {
              finalValue = dayjs().format(dateFormates);
            } else if (defaultDate && !field.value) {
              finalValue = defaultDate;
            }

            const parsedValue = safeParse(finalValue, dateFormates);

            let dynamicViews;
            let dynamicOpenTo;

            if (dateFormates === "MM") {
              dynamicViews = ["month"];
              dynamicOpenTo = "month";
            } else if (dateFormates === "YYYY") {
              dynamicViews = ["year"];
              dynamicOpenTo = "year";
            } else if (
              dateFormates === "MM/YYYY" ||
              dateFormates === "MM-YYYY" ||
              dateFormates === "MM/YY" ||
              dateFormates === "MM-YY"
            ) {
              dynamicViews = ["month", "year"];
            }

            return (
              <FormControl
                fullWidth={fullWidth}
                error={hasError}
                disabled={disabled}
              >
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label={label}
                    value={parsedValue}
                    onChange={(newValue) => {
                      const formatted = newValue?.isValid()
                        ? newValue.format(dateFormates)
                        : "";
                      field.onChange(formatted);
                      props.onChange?.(formatted);
                    }}
                    format={dateFormates}
                    disabled={disabled}
                    {...(dynamicViews ? { views: dynamicViews } : {})}
                    {...(dynamicOpenTo ? { openTo: dynamicOpenTo } : {})}
                                        slotProps={{
                      textField: {
                        size: "medium",
                        fullWidth,
                        placeholder: placeholder,
                        error: hasError,
                        helperText: hasError ? helperText : "",
                        onBlur: (e) => {
                          field.onBlur();
                          props.onBlur?.(e);
                        },
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "#c4c4c4",
                            },
                            "&:hover fieldset": {
                              borderColor: "#c4c4c4",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#c4c4c4",
                              borderWidth: "2px",
                            },
                          },
                        },
                        inputProps: {
                          readOnly: true,
                          onKeyDown: (e) => e.preventDefault(),
                          onPaste: (e) => e.preventDefault(),
                        },
                      },
                      openPickerButton: {
                        sx: {
                          display: disabled ? "none" : "flex",
                        },
                      },
                    }}
                    {...constraints}
                  />
                </LocalizationProvider>
              </FormControl>
            );
          }

          case "time": {
            const timeFormated = resolveTimeFormat(timeFormat, "hh:mm A");
            const ampm = !timeFormated.startsWith("HH");

            const now = dayjs();

            let valueToUse = field.value;
            if (!valueToUse && currentTime) {
              valueToUse = now.format(timeFormated);
            }
            if (!valueToUse && defaultTime) {
              valueToUse = defaultTime;
            }
            const parsedValue = safeParse(valueToUse, timeFormated);

            return (
              <FormControl fullWidth error={hasError} disabled={disabled}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <TimePicker
                    label={label}
                    value={parsedValue}
                    timeSteps={{ minutes: 1 }}
                    onChange={(nv) => {
                      const formatted = nv?.isValid()
                        ? nv.format(timeFormated)
                        : "";

                      field.onChange(formatted);
                      props.onChange?.(formatted);
                    }}
                    ampm={ampm}
                    format={timeFormated}
                                      slotProps={{
                      textField: {
                        size: "medium",
                        fullWidth,
                        placeholder: placeholder,
                        error: hasError,
                        helperText: hasError ? helperText : "",
                        onBlur: (e) => {
                          field.onBlur();
                          props.onBlur?.(e);
                        },
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "#c4c4c4",
                            },
                            "&:hover fieldset": {
                              borderColor: "#c4c4c4",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#c4c4c4",
                              borderWidth: "2px",
                            },
                          },
                        },
                        inputProps: {
                          readOnly: true,
                          onKeyDown: (e) => e.preventDefault(),
                          onPaste: (e) => e.preventDefault(),
                        },
                      },
                      openPickerButton: {
                        sx: {
                          display: disabled ? "none" : "flex",
                        },
                      },
                    }}
                  />
                </LocalizationProvider>
              </FormControl>
            );
          }

          case "datetime": {
            const dateFormated = normalizeDateFormat(combineDate);
            const timeFormate =
              combineTime === "HH:mm" || combineTime === "24_hour"
                ? "HH:mm"
                : "hh:mm A";

            const format = `${dateFormated} ${timeFormate}`;
            const ampm = timeFormate.includes("A");

            let finalValue = field.value;

            if (currentDateTime && !field.value) {
              finalValue = dayjs().format(format);
            } else if (defaultDateTime && !field.value) {
              finalValue = props.defaultDateTime;
            }

            const parsedValue = safeParse(finalValue, format);

            const now = dayjs();

            let minDT = minDateTime;
            let maxDT = maxDateTime;

            if (futureOnly) {
              minDT = now;
            }

            if (pastOnly) {
              maxDT = now;
            }
            // ── SINGLE DATETIME ───────────────────────────────
            return (
              <FormControl fullWidth error={hasError} disabled={disabled}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DateTimePicker
                    label={label}
                    value={parsedValue}
                    timeSteps={{ minutes: 1 }}
                    onChange={(nv) => {
                      const formatted = nv?.isValid() ? nv.format(format) : "";
                      field.onChange(formatted);
                      props.onChange?.(formatted);
                    }}
                    format={format}
                    ampm={ampm}
                    minDateTime={minDT}
                    maxDateTime={maxDT}
                    views={["year", "month", "day", "hours", "minutes"]}
                    openTo="day"
                      slotProps={{
                      textField: {
                        size: "medium",
                        fullWidth,
                        placeholder: placeholder,
                        error: hasError,
                        helperText: hasError ? helperText : "",
                        onBlur: (e) => {
                          field.onBlur();
                          props.onBlur?.(e);
                        },
                        sx: {
                          "& .MuiOutlinedInput-root": {
                            "& fieldset": {
                              borderColor: "#c4c4c4",
                            },
                            "&:hover fieldset": {
                              borderColor: "#c4c4c4",
                            },
                            "&.Mui-focused fieldset": {
                              borderColor: "#c4c4c4",
                              borderWidth: "2px",
                            },
                          },
                        },
                        inputProps: {
                          readOnly: true,
                          onKeyDown: (e) => e.preventDefault(),
                          onPaste: (e) => e.preventDefault(),
                        },
                      },
                      openPickerButton: {
                        sx: {
                          display: disabled ? "none" : "flex",
                        },
                      },
                    }}
                    shouldDisableTime={(timeValue, clockType) => {
                      if (!pastOnly) return false;

                      const selected = parsedValue;
                      if (!selected || !selected.isSame(now, "day"))
                        return false;

                      if (clockType === "hours") return timeValue > now.hour();

                      if (clockType === "minutes") {
                        if (selected.hour() === now.hour()) {
                          return timeValue > now.minute();
                        }
                      }

                      return false;
                    }}
                  />
                </LocalizationProvider>

                {/* {hasError && <FormHelperText>{props.helperText}</FormHelperText>} */}
              </FormControl>
            );
          }

          // ── Hyperlink ─────────────────────────────────────────────────────
          case "hyperlink":
            return (
              <FormControl fullWidth disabled={disabled}>
                <a
                  href={linkUrl || ""}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "#1976d2",
                    textDecoration: "underline",
                    fontSize: "14px",
                    fontWeight: 500,
                    cursor: linkUrl ? "pointer" : "not-allowed",
                    opacity: linkUrl ? 1 : 0.5,
                  }}
                >
                  {linkLabel || ""}
                </a>
                {hasError && <FormHelperText error>{helperText}</FormHelperText>}
              </FormControl>
            );

          // ── Default ───────────────────────────────────────────────────────
          default:
            return (
              <TextField
                {...field}
                type={type}
                label={label}
                placeholder={placeholder}
                fullWidth={fullWidth}
                error={hasError}
                helperText={helperText}
                disabled={disabled}
                {...props}
                onChange={(e) => {
                  field.onChange(e);
                  props.onChange?.(e);
                }}
                onBlur={(e) => {
                  field.onBlur();
                  props.onBlur?.(e);
                }}
              />
            );
        }
      },
      [
        type,
        label,
        placeholder,
        fullWidth,
        rows,
        disabled,
        options,
        showPassword,
        handleTogglePassword,
        name,
        props,
      ],
    );

    return (
      <Controller
        name={name}
        rules={rules}
        render={({ field, fieldState }) => (
          <Box
            sx={{
              marginBottom: nomargin
                ? 0
                : size === "small"
                ? ".5rem"
                : "1rem",
            }}
          >
            {inputLabelTop && (
              <Typography variant="p" className="customLabel inline-block">
                {inputLabelTop}
              </Typography>
            )}
            {renderField(field, fieldState)}
            {description && (
              <Box>
                <Typography
                  color="darkgray"
                  variant="caption"
                  className="mb-2 text-xs text-[#828282]"
                >
                  {description}
                </Typography>
              </Box>
            )}
          </Box>
        )}
      />
    );
  },
);

FormField.displayName = "FormField";
export default FormField;
